import { useState } from 'react';

import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';

import Iconify from 'src/components/iconify';

export type ExportFormat = 'full-width';

export type ExportOptions = {
    transparent: boolean;
    hideAxes: boolean;
};

type ExportFormatConfig = {
    value: ExportFormat;
    title: string;
    ratio: string;
    width: number;
    height: number;
};

export const EXPORT_FORMATS: ExportFormatConfig[] = [
    {
        value: 'full-width',
        title: 'Full width',
        ratio: '2.3:1 chart',
        width: 1840,
        height: 800,
    },
];

export const EXPORT_WIDTH = EXPORT_FORMATS[0].width;
export const EXPORT_HEIGHT = EXPORT_FORMATS[0].height;

type ExportCardProps = {
    title: string;
    ratio: string;
    format: ExportFormat;
    onExport: (
        format: ExportFormat,
        options: ExportOptions
    ) => void;
    exporting?: boolean;
};

function ExportCard({
    title,
    ratio,
    format,
    onExport,
    exporting = false,
}: ExportCardProps) {
    const [anchorEl, setAnchorEl] =
        useState<HTMLElement | null>(null);

    const menuOpen = Boolean(anchorEl);

    const previewDimensions: Record<
        ExportFormat,
        {
            width: string | number;
            height: string | number;
        }
    > = {
        'full-width': {
            width: '100%',
            height: 72,
        },
    };

    const dimensions =
        previewDimensions[format];

    const handleOpenMenu = (
        event: React.MouseEvent<HTMLElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();

        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleExportOption = (
        options: ExportOptions
    ) => {
        handleCloseMenu();

        onExport(format, options);
    };

    return (
        <Box>
            <Box
                sx={{
                    mb: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                        'space-between',
                }}
            >
                <Typography
                    sx={{
                        color: '#147EB3',
                        fontFamily:
                            'Public Sans',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '18px',
                    }}
                >
                    {title}
                </Typography>

                <IconButton
                    size="small"
                    disabled={exporting}
                    aria-label={`Export ${title}`}
                    aria-controls={
                        menuOpen
                            ? `export-menu-${format}`
                            : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={
                        menuOpen
                            ? 'true'
                            : undefined
                    }
                    onClick={handleOpenMenu}
                    sx={{
                        width: 24,
                        height: 24,
                        p: 0,
                        color: '#637381',

                        '&:hover': {
                            backgroundColor:
                                'rgba(145, 158, 171, 0.08)',
                        },
                    }}
                >
                    <Iconify
                        icon="akar-icons:download"
                        width={16}
                    />
                </IconButton>

                <Menu
                    id={`export-menu-${format}`}
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    sx={{
                        zIndex: (theme) =>
                            theme.zIndex.modal + 2,
                    }}
                    MenuListProps={{
                        sx: {
                            p: '4px',
                        },
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: '4px',
                                minWidth: '260px',
                                overflow: 'hidden',
                                border:
                                    '1px solid rgba(145, 158, 171, 0.20)',
                                borderRadius: '8px',
                                backgroundColor: '#FFFFFF',
                                boxShadow:
                                    '0px 8px 24px rgba(145, 158, 171, 0.24)',
                            },
                        },
                    }}
                >
                    <MenuItem
                        onClick={() =>
                            handleExportOption({
                                transparent: false,
                                hideAxes: false,
                            })
                        }
                        sx={{
                            minHeight: '34px',
                            px: '8px',
                            py: '6px',
                            borderRadius: '6px',
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth:
                                    '1px !important',
                                color: '#637381',
                            }}
                        >
                            <Iconify
                                icon="akar-icons:download"
                                width={16}
                            />
                        </ListItemIcon>

                        <ListItemText
                            primary="Download with background"
                            primaryTypographyProps={{
                                color: '#637381',
                                fontFamily:
                                    'Public Sans',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: '18px',
                            }}
                        />
                    </MenuItem>

                    <MenuItem
                        onClick={() =>
                            handleExportOption({
                                transparent: true,
                                hideAxes: false,
                            })
                        }
                        sx={{
                            minHeight: '34px',
                            px: '8px',
                            py: '6px',
                            borderRadius: '6px',
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth:
                                    '1px !important',
                                color: '#637381',
                            }}
                        >
                            <Iconify
                                icon="akar-icons:download"
                                width={16}
                            />
                        </ListItemIcon>

                        <ListItemText
                            primary="Download transparent background"
                            primaryTypographyProps={{
                                color: '#637381',
                                fontFamily:
                                    'Public Sans',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: '18px',
                            }}
                        />
                    </MenuItem>

                    <MenuItem
                        onClick={() =>
                            handleExportOption({
                                transparent: false,
                                hideAxes: true,
                            })
                        }
                        sx={{
                            minHeight: '34px',
                            px: '8px',
                            py: '6px',
                            borderRadius: '6px',
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth:
                                    '1px !important',
                                color: '#637381',
                            }}
                        >
                            <Iconify
                                icon="akar-icons:download"
                                width={16}
                            />
                        </ListItemIcon>

                        <ListItemText
                            primary="Download with background, no axes"
                            primaryTypographyProps={{
                                color: '#637381',
                                fontFamily:
                                    'Public Sans',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: '18px',
                            }}
                        />
                    </MenuItem>

                    <MenuItem
                        onClick={() =>
                            handleExportOption({
                                transparent: true,
                                hideAxes: true,
                            })
                        }
                        sx={{
                            minHeight: '34px',
                            px: '8px',
                            py: '6px',
                            borderRadius: '6px',
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth:
                                    '1px !important',
                                color: '#637381',
                            }}
                        >
                            <Iconify
                                icon="akar-icons:download"
                                width={16}
                            />
                        </ListItemIcon>

                        <ListItemText
                            primary="Download transparent background, no axes"
                            primaryTypographyProps={{
                                color: '#637381',
                                fontFamily:
                                    'Public Sans',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: '18px',
                            }}
                        />
                    </MenuItem>
                </Menu>
            </Box>

            <Box
                sx={{
                    height: 176,
                    p: '10px',
                    overflow: 'hidden',
                    border:
                        '1px solid #E4E9EE',
                    borderRadius: '4px',
                    backgroundColor:
                        '#FFFFFF',
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        p: '10px',
                        border:
                            '1px solid #E4E9EE',
                        backgroundColor:
                            '#FFFFFF',
                    }}
                >
                    <Box
                        sx={{
                            mb: '10px',
                            display: 'flex',
                            alignItems:
                                'flex-start',
                            justifyContent:
                                'space-between',
                        }}
                    >
                        <Box>
                            <Box
                                sx={{
                                    width: 54,
                                    height: 4,
                                    mb: '5px',
                                    borderRadius:
                                        '2px',
                                    backgroundColor:
                                        '#D6DCE1',
                                }}
                            />

                            <Box
                                sx={{
                                    width: 88,
                                    height: 9,
                                    borderRadius:
                                        '2px',
                                    backgroundColor:
                                        '#EEF1F4',
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                width: 28,
                                height: 9,
                                borderRadius:
                                    '2px',
                                backgroundColor:
                                    '#EEF1F4',
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            height: 108,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent:
                                'center',
                        }}
                    >
                        <Box
                            sx={{
                                width:
                                    dimensions.width,
                                height:
                                    dimensions.height,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent:
                                    'center',
                                backgroundColor:
                                    '#EAF4FC',
                                color: '#147EB3',
                                fontFamily:
                                    'Public Sans',
                                fontSize: '12px',
                                fontWeight: 600,
                                lineHeight: '16px',
                            }}
                        >
                            {ratio}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box >
    );
}

type ChartExportModalProps = {
    open: boolean;
    onClose: () => void;
    onExport: (
        format: ExportFormat,
        options: ExportOptions
    ) => void;
    exporting?: boolean;
};

export default function ChartExportModal({
    open,
    onClose,
    onExport,
    exporting = false,
}: ChartExportModalProps) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            disableEnforceFocus
            sx={{
                zIndex: (theme) =>
                    theme.zIndex.modal + 1,
            }}
            PaperProps={{
                sx: {
                    width: '640px',
                    maxWidth:
                        'calc(100vw - 48px)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor:
                        '#FFFFFF',
                    boxShadow:
                        '0px 24px 48px rgba(145, 158, 171, 0.24)',
                },
            }}
            BackdropProps={{
                sx: {
                    backgroundColor:
                        'rgba(33, 43, 54, 0.32)',
                },
            }}
        >
            <Box
                sx={{
                    height: '56px',
                    minHeight: '56px',
                    px: '20px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#FAFBFB',
                    borderBottom:
                        '1px solid rgba(145, 158, 171, 0.20)',
                }}
            >
                <Typography
                    sx={{
                        color: '#333C47',
                        fontFamily:
                            'Public Sans',
                        fontSize: '18px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '24px',
                    }}
                >
                    Export Chart
                </Typography>

                <IconButton
                    size="small"
                    aria-label="Close export modal"
                    onClick={onClose}
                    sx={{
                        color: '#637381',
                    }}
                >
                    <Iconify
                        icon="mingcute:close-line"
                        width={20}
                    />
                </IconButton>
            </Box>

            <Box
                sx={{
                    px: '20px',
                    py: '12px',
                    borderBottom:
                        '1px solid rgba(145, 158, 171, 0.12)',
                }}
            >
                <Typography
                    sx={{
                        color: '#333C47',
                        fontFamily:
                            'Public Sans',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '18px',
                    }}
                >
                    Select any format according
                    to your needs
                </Typography>
            </Box>

            <DialogContent
                sx={{
                    p: '16px 20px 20px',
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '20px',
                    }}
                >
                    {EXPORT_FORMATS.map(
                        (exportFormat) => (
                            <ExportCard
                                key={
                                    exportFormat.value
                                }
                                title={
                                    exportFormat.title
                                }
                                ratio={
                                    exportFormat.ratio
                                }
                                format={
                                    exportFormat.value
                                }
                                onExport={
                                    onExport
                                }
                                exporting={exporting}
                            />
                        )
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}